<template>
  <Modal
    :visible="visible"
    :title="title"
    width="720px"
    :close-on-backdrop="closable"
    :close-on-escape="closable"
    :show-close-button="closable"
    initial-focus-selector="[data-review-cancel]"
    @close="requestClose"
  >
    <div class="account-deletion-review" data-account-deletion-review>
      <section
        v-if="activeDeletion"
        class="account-deletion-review__progress"
        data-deletion-progress
      >
        <p class="account-deletion-review__lead">{{ progressHeading }}</p>
        <PercentageMeter
          :percentage="progressPercentage"
          :label="progressLabel"
        />
        <p
          class="account-deletion-review__muted account-deletion-review__step"
          aria-live="polite"
          data-deletion-step
        >
          Step {{ progressStep }} of {{ activeDeletion.stageCount }}
        </p>
        <p class="account-deletion-review__notice" data-deletion-close-notice>
          {{ closeNotice }}
        </p>
      </section>

      <LoadingState
        v-else-if="loading"
        variant="section"
        message="Checking what will be deleted…"
      />

      <div
        v-else-if="loadError"
        class="account-deletion-review__state"
        role="alert"
      >
        <p>{{ loadError }}</p>
        <UnitButton variant="secondary" size="small" @click="loadPreview()">
          Retry
        </UnitButton>
      </div>

      <template v-else-if="preview">
        <div class="account-deletion-review__warning">
          <p class="account-deletion-review__lead">{{ leadText }}</p>
          <p
            v-if="mode === 'admin' && preview.subject.email"
            class="account-deletion-review__subject"
          >
            {{ preview.subject.email }}
          </p>
        </div>

        <p
          v-if="changedNotice"
          class="account-deletion-review__notice"
          role="status"
          data-impact-changed-notice
        >
          {{ changedNotice }}
        </p>

        <section
          class="account-deletion-review__section"
          :aria-labelledby="organisationsHeadingId"
        >
          <h4 :id="organisationsHeadingId" class="account-deletion-review__heading">
            Organisations and money
          </h4>
          <p
            v-if="preview.organisations.length === 0"
            class="account-deletion-review__muted"
          >
            {{
              mode === "self"
                ? "You are not a member of any organisation."
                : "This account is not a member of any organisation."
            }}
          </p>
          <ul v-else class="account-deletion-review__organisations">
            <li
              v-for="organisation in preview.organisations"
              :key="organisation.orgId"
              class="account-deletion-review__organisation"
              data-review-organisation
            >
              <h5 class="account-deletion-review__organisation-name">
                {{ organisation.name }}
              </h5>
              <dl class="account-deletion-review__amounts">
                <div>
                  <dt>Current</dt>
                  <dd>{{ formatAmount(organisation, organisation.currentAtomic) }}</dd>
                </div>
                <div>
                  <dt>Savings</dt>
                  <dd>{{ formatAmount(organisation, organisation.savingsAtomic) }}</dd>
                </div>
                <div class="account-deletion-review__amount-total">
                  <dt>Money that will be deleted</dt>
                  <dd data-review-total>{{ formatTotal(organisation) }}</dd>
                </div>
              </dl>
              <p
                v-if="organisation.willClose"
                class="account-deletion-review__closure"
                data-review-closure
              >
                This organisation will be closed because {{ closureSubject }}
                its last member.
              </p>
              <SuccessorNominationPanel
                v-if="organisation.needsSuccessor"
                v-model="successors[organisation.orgId]"
                :org-id="organisation.orgId"
                :organisation-name="organisation.name"
                :subject-uid="mode === 'admin' ? preview.subject.uid : null"
                :suggested-successor="organisation.suggestedSuccessor"
              />
            </li>
          </ul>
        </section>

        <p class="account-deletion-review__policy">
          Messages this account sent will be replaced by a placeholder. People
          they talked to keep the messages they sent.
          <RouterLink
            to="/legal/data-policy"
            target="_blank"
            rel="noopener"
            class="account-deletion-review__link"
          >
            Read the data policy
          </RouterLink>
        </p>

        <label class="account-deletion-review__acknowledge">
          <input
            v-model="acknowledged"
            type="checkbox"
            :disabled="busy"
            data-review-acknowledge
          />
          <span>I understand this cannot be undone</span>
        </label>

        <p
          v-if="missingSuccessorCount > 0"
          class="account-deletion-review__muted"
          data-review-successor-hint
        >
          Nominate a new admin for
          {{ missingSuccessorCount === 1 ? "1 organisation" : `${missingSuccessorCount} organisations` }}
          before you continue.
        </p>

        <p
          v-if="errorMessage"
          class="account-deletion-review__error"
          role="alert"
          data-review-error
        >
          {{ errorMessage }}
        </p>
      </template>
    </div>

    <template #footer>
      <div v-if="activeDeletion" class="account-deletion-review__actions">
        <UnitButton
          variant="secondary"
          data-review-cancel
          data-deletion-close
          @click="requestClose"
        >
          Close
        </UnitButton>
      </div>
      <div v-else class="account-deletion-review__actions">
        <UnitButton
          variant="secondary"
          :disabled="busy"
          data-review-cancel
          @click="requestClose"
        >
          Cancel
        </UnitButton>
        <UnitButton
          variant="danger"
          :disabled="!canConfirm"
          :loading="busy"
          loading-label="Deleting…"
          data-review-confirm
          @click="confirm"
        >
          Delete account
        </UnitButton>
      </div>
    </template>
  </Modal>
</template>

<script setup>
import { computed, reactive, ref, watch } from "vue";

import Modal from "@/components/GenericComponents/Modal.vue";
import LoadingState from "@/components/GenericComponents/LoadingState.vue";
import PercentageMeter from "@/components/GenericComponents/PercentageMeter.vue";
import UnitButton from "@/components/GenericComponents/UnitButton.vue";
import SuccessorNominationPanel from "@/components/OrgComponents/SuccessorNominationPanel.vue";
import {
  accountAdministrationService,
  createAccountAdministrationOperationId,
  describeAccountAdministrationError,
} from "@/services/accountAdministrationService";
import {
  ACCOUNT_DELETION_STAGE_LABELS,
  accountDeletionProgressTracker,
} from "@/services/accountDeletionProgressTracker";
import { auth } from "@/firebase/authorityClient";
import { formatAtomicCurrency } from "@/utils/atomicCurrencyFormatting";

defineOptions({ name: "AccountDeletionReview" });

let instanceCount = 0;

const props = defineProps({
  visible: { type: Boolean, default: false },
  mode: {
    type: String,
    default: "self",
    validator: (value) => ["self", "admin"].includes(value),
  },
  target: { type: Object, default: null },
  // Self mode only: runs immediately before the destructive command, for
  // example to re-authenticate. Throw to abort; `userMessage` is displayed.
  beforeConfirm: { type: Function, default: null },
  tracker: { type: Object, default: () => accountDeletionProgressTracker },
});

const emit = defineEmits(["deleted", "close"]);

const instanceId = ++instanceCount;
const organisationsHeadingId = `account-deletion-review-orgs-${instanceId}`;

const preview = ref(null);
const loading = ref(false);
const loadError = ref("");
const busy = ref(false);
const acknowledged = ref(false);
const errorMessage = ref("");
const changedNotice = ref("");
const successors = reactive({});
// The deletion in progress for this dialog's target. It outlives the dialog:
// closing lets the server finish, and the tracker announces the outcome.
const activeDeletion = ref(null);
let activeTargetKey = null;
const operationIds = new Map();
let generation = 0;

const targetKey = () => `${props.mode}:${JSON.stringify(props.target || null)}`;

const closable = computed(() => !busy.value || Boolean(activeDeletion.value));

const progressHeading = computed(() =>
  props.mode === "self"
    ? "Deleting your account…"
    : `Deleting ${activeDeletion.value?.subjectLabel || "the account"}'s account…`
);

const progressLabel = computed(() => {
  const deletion = activeDeletion.value;
  if (!deletion) return "";
  if (deletion.state === "completed") return "Account deleted";
  return ACCOUNT_DELETION_STAGE_LABELS[deletion.stage] || "Deleting the account";
});

const progressPercentage = computed(() => {
  const deletion = activeDeletion.value;
  if (!deletion || deletion.stageCount === 0) return 0;
  if (deletion.state === "completed") return 100;
  return Math.max(4, Math.round((deletion.stageIndex / deletion.stageCount) * 100));
});

const progressStep = computed(() => {
  const deletion = activeDeletion.value;
  if (!deletion) return 0;
  return Math.min(deletion.stageIndex + 1, deletion.stageCount);
});

const closeNotice = computed(() =>
  props.mode === "self"
    ? "You can close this window. The deletion keeps running, and you will be signed out when it finishes."
    : "You can close this window. The deletion keeps running, and we will show you a notice when it finishes, even if you come back later."
);

const title = computed(() =>
  props.mode === "self" ? "Delete your account" : "Delete account"
);

const subjectName = computed(() => {
  const subject = preview.value?.subject;
  return subject?.displayName || subject?.email || "this account";
});

const leadText = computed(() =>
  props.mode === "self"
    ? "This permanently deletes your account, your profile, your memberships, your products, services and media, and every Wallet you hold."
    : `This permanently deletes ${subjectName.value}'s account, profile, memberships, products, services, media and Wallets.`
);

const closureSubject = computed(() =>
  props.mode === "self" ? "you are" : `${subjectName.value} is`
);

const organisationsNeedingSuccessor = computed(() =>
  (preview.value?.organisations || []).filter(
    (organisation) => organisation.needsSuccessor
  )
);

const missingSuccessorCount = computed(
  () =>
    organisationsNeedingSuccessor.value.filter(
      ({ orgId }) => !(successors[orgId]?.length > 0)
    ).length
);

const canConfirm = computed(
  () =>
    Boolean(preview.value) &&
    !loading.value &&
    !busy.value &&
    acknowledged.value &&
    missingSuccessorCount.value === 0
);

const formatAmount = (organisation, amountAtomic) => {
  try {
    return formatAtomicCurrency({
      amountAtomic,
      amountScale: organisation.amountScale,
      currencySymbol: organisation.currency.symbol,
    });
  } catch (_error) {
    return "Unavailable";
  }
};

const formatTotal = (organisation) =>
  formatAmount(
    organisation,
    (
      BigInt(organisation.currentAtomic) + BigInt(organisation.savingsAtomic)
    ).toString()
  );

const successorPayload = () =>
  Object.fromEntries(
    organisationsNeedingSuccessor.value.map(({ orgId }) => [
      orgId,
      [...(successors[orgId] || [])],
    ])
  );

const operationIdFor = (impactRef) => {
  if (!operationIds.has(impactRef)) {
    operationIds.set(
      impactRef,
      createAccountAdministrationOperationId(
        props.mode === "self" ? "delete_own" : "delete_user"
      )
    );
  }
  return operationIds.get(impactRef);
};

const resetState = () => {
  generation += 1;
  preview.value = null;
  loading.value = false;
  loadError.value = "";
  acknowledged.value = false;
  errorMessage.value = "";
  changedNotice.value = "";
  Object.keys(successors).forEach((orgId) => delete successors[orgId]);
};

const loadPreview = async ({ changed = false } = {}) => {
  const requestGeneration = ++generation;
  loading.value = true;
  loadError.value = "";
  errorMessage.value = "";
  try {
    const next =
      props.mode === "self"
        ? await accountAdministrationService.previewOwnAccountDeletion()
        : await accountAdministrationService.previewAccountDeletion(props.target);
    if (requestGeneration !== generation) return;
    const stillNeeded = new Set(
      next.organisations
        .filter((organisation) => organisation.needsSuccessor)
        .map(({ orgId }) => orgId)
    );
    Object.keys(successors).forEach((orgId) => {
      if (!stillNeeded.has(orgId)) delete successors[orgId];
    });
    stillNeeded.forEach((orgId) => {
      if (!Array.isArray(successors[orgId])) successors[orgId] = [];
    });
    preview.value = next;
    if (changed) {
      acknowledged.value = false;
      changedNotice.value =
        "Balances or memberships changed while you were reviewing. Check the updated amounts and confirm again.";
    }
  } catch (error) {
    if (requestGeneration !== generation) return;
    preview.value = null;
    loadError.value = describeAccountAdministrationError(error);
  } finally {
    if (requestGeneration === generation) loading.value = false;
  }
};

const confirm = async () => {
  if (!canConfirm.value) return;
  const current = preview.value;
  busy.value = true;
  errorMessage.value = "";
  try {
    if (props.mode === "self" && props.beforeConfirm) {
      await props.beforeConfirm();
    }
  } catch (error) {
    errorMessage.value =
      error?.userMessage || "The account could not be deleted. Please try again.";
    busy.value = false;
    return;
  }
  const intent = {
    impactRef: current.impactRef,
    successors: successorPayload(),
    operationId: operationIdFor(current.impactRef),
  };
  const deletion = props.tracker.start({
    mode: props.mode,
    uid: current.subject.uid,
    subjectLabel: current.subject.displayName || current.subject.email,
    operationId: intent.operationId,
    adminUid: props.mode === "admin" ? auth.currentUser?.uid || null : null,
    run: () =>
      props.mode === "self"
        ? accountAdministrationService.deleteOwnAccount(intent)
        : accountAdministrationService.deleteUserAccount({
            uid: current.subject.uid,
            ...intent,
          }),
  });
  activeDeletion.value = deletion;
  activeTargetKey = targetKey();
  const outcome = await deletion.done;
  const stillShown = activeDeletion.value === deletion;
  if (stillShown) {
    activeDeletion.value = null;
    activeTargetKey = null;
  }
  busy.value = false;
  if (outcome.state === "completed") {
    // current is false when the dialog has since moved on to another target.
    emit("deleted", { mode: props.mode, subject: current.subject, current: stillShown });
    return;
  }
  if (!stillShown) return;
  if (outcome.reason === "impact-changed") {
    await loadPreview({ changed: true });
    return;
  }
  errorMessage.value = outcome.errorMessage;
};

const requestClose = () => {
  if (closable.value) emit("close");
};

watch(
  () => [props.visible, props.mode, JSON.stringify(props.target || null)],
  ([isVisible]) => {
    // Reopening the same target while its deletion runs shows the progress
    // again instead of a fresh review.
    if (activeDeletion.value?.state === "running") {
      if (!isVisible || activeTargetKey === targetKey()) return;
      activeDeletion.value = null;
      activeTargetKey = null;
      busy.value = false;
    }
    resetState();
    if (isVisible) loadPreview();
  },
  { immediate: true }
);
</script>

<style scoped>
.account-deletion-review {
  display: grid;
  gap: var(--space-6);
  min-width: 0;
  color: var(--color-text-primary);
}

.account-deletion-review p,
.account-deletion-review h4,
.account-deletion-review h5,
.account-deletion-review dl,
.account-deletion-review dd {
  margin: 0;
}

.account-deletion-review__warning {
  display: grid;
  gap: var(--space-2);
  padding: var(--space-5);
  border-radius: var(--radius-sm);
  background: var(--color-feedback-danger-confirm-surface);
  color: var(--color-feedback-danger-foreground);
}

.account-deletion-review__lead {
  font-weight: var(--font-weight-medium);
  overflow-wrap: anywhere;
}

.account-deletion-review__subject {
  font-size: var(--font-size-label);
  overflow-wrap: anywhere;
}

.account-deletion-review__notice {
  padding: var(--space-4) var(--space-5);
  border-radius: var(--radius-sm);
  background: var(--color-feedback-warning-surface);
  color: var(--color-text-primary);
  font-size: var(--font-size-label);
}

.account-deletion-review__section {
  display: grid;
  gap: var(--space-4);
  min-width: 0;
}

.account-deletion-review__heading {
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-semibold);
}

.account-deletion-review__organisations {
  display: grid;
  gap: var(--space-5);
  margin: 0;
  padding: 0;
  list-style: none;
}

.account-deletion-review__organisation {
  display: grid;
  gap: var(--space-4);
  min-width: 0;
  padding: var(--space-5);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-sm);
}

.account-deletion-review__organisation-name {
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-semibold);
  overflow-wrap: anywhere;
}

.account-deletion-review__amounts {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
  gap: var(--space-4);
}

.account-deletion-review__amounts dt {
  color: var(--color-text-secondary);
  font-size: var(--font-size-caption);
}

.account-deletion-review__amounts dd {
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
}

.account-deletion-review__amount-total dd {
  color: var(--color-feedback-danger-foreground);
  font-weight: var(--font-weight-semibold);
}

.account-deletion-review__closure {
  color: var(--color-feedback-danger-foreground);
  font-size: var(--font-size-label);
  font-weight: var(--font-weight-medium);
}

.account-deletion-review__policy,
.account-deletion-review__muted,
.account-deletion-review__state {
  color: var(--color-text-secondary);
  font-size: var(--font-size-label);
}

.account-deletion-review__state {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-4);
}

.account-deletion-review__link {
  color: var(--color-text-primary);
  text-decoration: underline;
}

.account-deletion-review__link:hover {
  color: var(--color-accent-primary);
}

.account-deletion-review__link:focus-visible {
  outline: var(--focus-ring-width) solid var(--focus-ring-color);
  outline-offset: var(--focus-ring-offset);
}

.account-deletion-review__acknowledge {
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  margin: 0;
  text-align: left;
  gap: var(--space-4);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
}

.account-deletion-review__acknowledge input {
  flex: 0 0 auto;
  width: var(--control-height-micro);
  height: var(--control-height-micro);
  min-height: 0;
  margin: 0;
  accent-color: var(--color-feedback-danger-solid);
}

.account-deletion-review__acknowledge input:focus-visible {
  outline: var(--focus-ring-width) solid var(--focus-ring-color);
  outline-offset: var(--focus-ring-offset);
}

.account-deletion-review__error {
  color: var(--color-feedback-danger-foreground);
  font-size: var(--font-size-label);
}

.account-deletion-review__progress {
  display: grid;
  gap: var(--space-5);
  min-width: 0;
}

.account-deletion-review__step {
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.account-deletion-review__actions {
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--space-4);
}
</style>
